import type { MovementId } from '../config/movements';

/** Parsed customer row from SAP `fetchCustDetails` / RFID `/customer_details`. */
export type CustomerDetailsRow = {
  customerCode: string;
  customerName: string;
  lob: string;
  lobText: string;
  accountGroup: string;
};

export type CustomerPostingContext = {
  lob: string;
  lobText: string;
  accountGroup: string;
  /** SAP LOB code when present (e.g. 01 Auto, 02 FMCG). */
  lobCode: string;
  useAutoTransferType: boolean;
};

function normalizeLocationRows(raw: unknown): Record<string, unknown>[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  return [raw as Record<string, unknown>];
}

/** SAP fetchCustDetails expects customer code without leading zero padding. */
export function customerCodeForSapApi(code: string): string {
  const digits = code.replace(/\D/g, '');
  if (!digits) return code.trim();
  const withoutLeadingZeros = digits.replace(/^0+/, '');
  return withoutLeadingZeros || '0';
}

function padCustomerCode(code: string): string {
  const digits = code.replace(/\D/g, '');
  if (!digits) return code.trim();
  return digits.padStart(10, '0');
}

function codesMatch(a: string, b: string): boolean {
  const pa = padCustomerCode(a);
  const pb = padCustomerCode(b);
  const sa = customerCodeForSapApi(a);
  const sb = customerCodeForSapApi(b);
  return pa === pb || sa === sb || a.trim() === b.trim();
}

function nonEmptyCode(value: unknown): string {
  const s = String(value ?? '').trim();
  return s || '';
}

function normalizeAccountGroup(accountGroup: string): string {
  const trimmed = accountGroup.trim().toUpperCase();
  const match = trimmed.match(/^(Z\d{3})\b/);
  return match ? match[1] : trimmed;
}

/**
 * Normalize LOB from SAP fields for posting rules.
 * User rules use "Auto" and "FMCG" labels; SAP may send codes (01/02) or text.
 */
export function normalizeCustomerLob(lob: string, lobText = ''): 'AUTO' | 'FMCG' | 'OTHER' {
  const text = `${lobText} ${lob}`.trim().toUpperCase();
  if (text.includes('FMCG') || lob.trim() === '02') return 'FMCG';
  if (text.includes('AUTO') || lob.trim() === '01' || lob.trim() === '1') return 'AUTO';
  return 'OTHER';
}

/**
 * Auto vs manual SAP transfer type from LOB + Account_Group (not kit structure).
 *
 * - LOB Auto + Z001 → auto (LEAPOUTAUT, etc.)
 * - LOB Auto + Z006 → non-auto
 * - LOB FMCG → non-auto
 */
export function resolveUseAutoTransferType(lob: string, accountGroup: string, lobText = ''): boolean {
  const lobKey = normalizeCustomerLob(lob, lobText);
  const ag = normalizeAccountGroup(accountGroup);
  if (lobKey === 'FMCG') return false;
  if (lobKey === 'AUTO' && ag === 'Z001') return true;
  if (lobKey === 'AUTO' && ag === 'Z006') return false;
  return false;
}

/** Map normalized LOB to SAP LoB code for transfer-out payloads. */
export function lobCodeForPosting(lob: string, lobText = ''): string {
  const key = normalizeCustomerLob(lob, lobText);
  if (key === 'AUTO') return '01';
  if (key === 'FMCG') return '02';
  const trimmed = lob.trim();
  return /^\d+$/.test(trimmed) ? trimmed : '';
}

export function parseCustomerDetailsResponse(
  data: unknown,
  requestedCustomerCode: string
): CustomerDetailsRow | null {
  const d = data as Record<string, unknown>;
  const rd = (d.Response_Data ?? d.Response) as Record<string, unknown> | undefined;
  const raw = rd?.item ?? rd?.Item;
  const rows = normalizeLocationRows(raw);
  if (rows.length === 0) return null;

  const match =
    rows.find((r) =>
      codesMatch(
        String(r.CustomerCode ?? r.Customer_No ?? r.Customer ?? ''),
        requestedCustomerCode
      )
    ) ?? rows[0];

  const row: CustomerDetailsRow = {
    customerCode: String(match.CustomerCode ?? match.Customer_No ?? requestedCustomerCode),
    customerName: String(match.CustomerName ?? match.CustName ?? ''),
    lob: String(match.LOB ?? match.LoB ?? ''),
    lobText: String(match.LOB_Text ?? match.LOB_DESC ?? match.LOB ?? ''),
    accountGroup: String(
      match.Account_Group ??
        match.AccountGroup ??
        match.KTOKD ??
        match.Account_group ??
        ''
    ),
  };
  return row;
}

/** Customer on the sales order / transfer — movement-aware (receiver vs sender). */
export function customerCodeFromDocument(
  doc: { Receiver?: unknown; Customer?: unknown; Sender?: unknown },
  movementId?: MovementId
): string {
  const receiver = nonEmptyCode(doc.Receiver);
  const customer = nonEmptyCode(doc.Customer);
  const sender = nonEmptyCode(doc.Sender);

  let picked: string;
  if (movementId === 'dehire' || movementId === 'dehire_received') {
    picked = sender || customer || receiver;
  } else if (movementId === 'custin') {
    picked = customer || sender || receiver;
  } else {
    picked = receiver || customer || sender;
  }

  return picked;
}

export function buildCustomerPostingContext(row: CustomerDetailsRow): CustomerPostingContext {
  const lobCode = lobCodeForPosting(row.lob, row.lobText);
  return {
    lob: row.lob,
    lobText: row.lobText,
    accountGroup: row.accountGroup,
    lobCode,
    useAutoTransferType: resolveUseAutoTransferType(row.lob, row.accountGroup, row.lobText),
  };
}
